import { cn } from "@/utils";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CalendarDays, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Button } from "./Button";
import { Calendar } from "./Calendar";
import { FieldShell } from "./Field";
import { controlBase } from "./field.styles";

/**
 * Campo de data: gatilho com a data escrita em pt-BR e o `Calendar` da Fidexa.
 *
 * Nunca `<input type="date">`. O nativo muda de cara em cada navegador, e no
 * Chrome ele exibe `dd/mm/yyyy` com jeito de instrução — o lojista tenta digitar
 * ali e descobre que a ordem dos campos é outra. Aqui a data aparece pronta
 * (`18/09/2026`) e a escolha é sempre pelo calendário.
 *
 * Abaixo de 640px o calendário vira folha de baixo, como no `Select`: menu
 * ancorado num campo perto do rodapé nasceria fora da tela.
 */

export interface DateFieldProps {
  label: string;
  value: Date | null;
  onValueChange: (date: Date | null) => void;
  min?: Date | null;
  max?: Date | null;
  /** Realce contínuo entre duas datas — o par "De" e "Até" de um período. */
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  placeholder?: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  /** Botão de limpar no canto do campo. Ligado por padrão quando há data. */
  clearable?: boolean;
  containerClassName?: string;
  className?: string;
}

const escrever = (data: Date | null) =>
  data
    ? data.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

export const DateField = ({
  label,
  value,
  onValueChange,
  min,
  max,
  rangeStart,
  rangeEnd,
  placeholder = "Selecione a data",
  hint,
  error,
  required,
  disabled,
  hideLabel,
  clearable = true,
  containerClassName,
  className,
}: DateFieldProps) => {
  const idBase = useId();
  const triggerId = `${idBase}-gatilho`;
  const hintId = `${idBase}-dica`;
  const errorId = `${idBase}-erro`;

  const [aberto, setAberto] = useState(false);
  const [telaGrande, setTelaGrande] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 40rem)").matches
  );
  const [paraCima, setParaCima] = useState(false);
  // Ancorado no campo é o padrão. Quando o calendário não cabe nem acima nem
  // abaixo — um modal estreito, uma janela baixa — ele se solta do campo em vez
  // de nascer cortado pela borda do container.
  const [solto, setSolto] = useState(false);
  // O calendário é mais largo que o campo. Ancorado pela esquerda, um campo
  // encostado na borda direita da tela (o "Até" de um período, por exemplo)
  // abre metade do calendário para fora — e não há para onde rolar. Nesse
  // caso ele passa a alinhar pela direita, crescendo para o lado que tem
  // espaço.
  const [alinhadoADireita, setAlinhadoADireita] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const semMovimento = useReducedMotion();
  const duracao = (ms: number) => (semMovimento ? 0 : ms / 1000);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 40rem)");
    const aoMudar = () => setTelaGrande(mq.matches);
    mq.addEventListener("change", aoMudar);
    return () => mq.removeEventListener("change", aoMudar);
  }, []);

  /**
   * Abre para cima quando não cabe embaixo — medindo pelo container que rola, e
   * não pela janela: dentro de um modal, a janela mente sobre o espaço que
   * existe de verdade.
   */
  const medirEspaco = useCallback(() => {
    const gatilho = triggerRef.current;
    if (!gatilho || !telaGrande) return;

    // Altura medida, não estimada: o calendário tem cinco ou seis semanas
    // conforme o mês, e a constante que existia aqui (380) errava justamente
    // nos meses de seis semanas — dizia que cabia e ele nascia cortado.
    const altura = popoverRef.current?.offsetHeight ?? 0;
    if (!altura) return;

    const recorte = (() => {
      let elemento = gatilho.parentElement;
      while (elemento) {
        if (/(auto|scroll|hidden)/.test(getComputedStyle(elemento).overflowY)) {
          return elemento.getBoundingClientRect();
        }
        elemento = elemento.parentElement;
      }
      return null;
    })();

    const limiteInferior = recorte
      ? Math.min(window.innerHeight, recorte.bottom)
      : window.innerHeight;
    const limiteSuperior = recorte ? Math.max(0, recorte.top) : 0;

    const RESPIRO = 8;
    const { top, bottom } = gatilho.getBoundingClientRect();
    const abaixo = limiteInferior - bottom - RESPIRO;
    const acima = top - limiteSuperior - RESPIRO;

    setSolto(altura > abaixo && altura > acima);
    setParaCima(altura > abaixo && acima > abaixo);

    // Mesma conta na horizontal, contra o container que recorta de lado (ou a
    // janela, quando não há um).
    const recorteHorizontal = (() => {
      let elemento = gatilho.parentElement;
      while (elemento) {
        if (/(auto|scroll|hidden)/.test(getComputedStyle(elemento).overflowX)) {
          return elemento.getBoundingClientRect();
        }
        elemento = elemento.parentElement;
      }
      return null;
    })();

    const largura = popoverRef.current?.offsetWidth ?? 0;
    const { left, right } = gatilho.getBoundingClientRect();
    const limiteDireito = recorteHorizontal
      ? Math.min(window.innerWidth, recorteHorizontal.right)
      : window.innerWidth;
    const limiteEsquerdo = recorteHorizontal
      ? Math.max(0, recorteHorizontal.left)
      : 0;

    // Só troca de lado se do outro couber: num campo espremido dos dois lados,
    // continuar à esquerda ao menos mantém o começo do mês visível.
    const cabeADireita = left + largura + RESPIRO <= limiteDireito;
    const cabeAEsquerda = right - largura - RESPIRO >= limiteEsquerdo;
    setAlinhadoADireita(!cabeADireita && cabeAEsquerda);
  }, [telaGrande]);

  useEffect(() => {
    if (!aberto) return;
    medirEspaco();
    window.addEventListener("resize", medirEspaco);
    window.addEventListener("scroll", medirEspaco, true);
    return () => {
      window.removeEventListener("resize", medirEspaco);
      window.removeEventListener("scroll", medirEspaco, true);
    };
  }, [aberto, medirEspaco]);

  // Clique fora e Esc fecham; o foco volta para o gatilho, senão quem usa
  // teclado é devolvido ao começo da página.
  useEffect(() => {
    if (!aberto) return;

    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      // O popover checa separado: solto do campo, ele é levado para o `body`
      // por portal e deixa de estar dentro do wrapper.
      if (wrapperRef.current?.contains(alvo)) return;
      if (popoverRef.current?.contains(alvo)) return;
      setAberto(false);
    };
    const aoTeclar = (evento: globalThis.KeyboardEvent) => {
      if (evento.key !== "Escape") return;
      evento.stopPropagation();
      setAberto(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("keydown", aoTeclar, true);
    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("keydown", aoTeclar, true);
    };
  }, [aberto]);

  const escolher = (data: Date) => {
    onValueChange(data);
    setAberto(false);
    triggerRef.current?.focus();
  };

  const aoTeclarNoGatilho = (evento: KeyboardEvent<HTMLButtonElement>) => {
    if (
      evento.key === "ArrowDown" ||
      evento.key === "Enter" ||
      evento.key === " "
    ) {
      evento.preventDefault();
      setAberto(true);
    }
  };

  const ancorado = telaGrande && !solto;
  const temData = !!value;

  return (
    <FieldShell
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      required={required}
      controlId={triggerId}
      hintId={hintId}
      errorId={errorId}
      className={containerClassName}
    >
      <div ref={wrapperRef} className="relative">
        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={aberto}
          data-popover-aberto={aberto || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          onClick={() => setAberto(anterior => !anterior)}
          onKeyDown={aoTeclarNoGatilho}
          className={cn(
            controlBase,
            "flex min-h-12 cursor-pointer items-center gap-2 px-3 text-left",
            className
          )}
        >
          <CalendarDays
            aria-hidden="true"
            className="text-ds-ink-600 size-5 shrink-0"
          />
          <span
            className={cn(
              "flex-1 truncate",
              temData ? "ds-numeric text-ds-ink-900" : "text-ds-ink-600"
            )}
          >
            {temData ? escrever(value) : placeholder}
          </span>
        </button>

        {/* O limpar fica fora do gatilho: botão dentro de botão não é HTML
            válido, e o leitor de tela anunciaria um só. */}
        {clearable && temData && !disabled ? (
          <button
            type="button"
            onClick={() => onValueChange(null)}
            aria-label={`Limpar ${label.toLowerCase()}`}
            className="text-ds-ink-600 hover:text-ds-ink-900 focus-visible:ring-ds-focus focus-visible:ring-offset-ds-surface rounded-ds-sm absolute top-1/2 right-1 inline-flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        ) : null}

        <AnimatePresence>
          {aberto ? (
            // `motion` com `key` como filho direto: com um Fragment aqui, o
            // framer não sabe quando a saída terminou e deixa o popover
            // pendurado no DOM, invisível mas ainda no leitor de tela.
            <motion.div key="popover">
              {/* Fundo só no celular, acompanhando a folha de baixo. */}
              <motion.div
                key="veu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: duracao(180) }}
                onClick={() => setAberto(false)}
                className={cn(
                  "bg-ds-scrim/40 fixed inset-0 z-40",
                  ancorado && "sm:hidden"
                )}
              />

              <motion.div
                key="painel"
                ref={popoverRef}
                role="dialog"
                aria-label={label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{
                  duration: duracao(180),
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={
                  ancorado
                    ? {
                        top: paraCima ? "auto" : "calc(100% + 4px)",
                        bottom: paraCima ? "calc(100% + 4px)" : "auto",
                      }
                    : undefined
                }
                className={cn(
                  // Celular: folha de baixo, com área segura.
                  "rounded-t-ds-xl border-ds-border fixed inset-x-0 bottom-0 z-50 border-t",
                  "bg-ds-surface shadow-ds-sheet pb-[env(safe-area-inset-bottom)]",
                  ancorado
                    ? // Desktop com espaço: ancorado no campo.
                      cn(
                        "sm:rounded-ds-lg sm:shadow-ds-overlay sm:absolute sm:inset-x-auto sm:bottom-auto sm:w-auto sm:border",
                        alinhadoADireita ? "sm:right-0" : "sm:left-0"
                      )
                    : // Desktop sem espaço: centralizado, livre do recorte do
                      // container. Melhor ver o calendário inteiro fora do
                      // campo do que metade dele colada nele.
                      "sm:rounded-ds-lg sm:shadow-ds-overlay sm:fixed sm:top-1/2 sm:bottom-auto sm:left-1/2 sm:w-auto sm:-translate-x-1/2 sm:-translate-y-1/2 sm:border"
                )}
              >
                <Calendar
                  autoFocus
                  value={value}
                  onSelect={escolher}
                  min={min}
                  max={max}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  label={label}
                />

                {/* Solto do campo — folha no celular, centralizado no desktop —
                    o calendário tem cara de modal, e modal sem botão de fechar
                    deixa quem abriu sem querer procurando a saída. Ancorado no
                    campo não precisa: ali clicar fora é o gesto óbvio. */}
                {!ancorado ? (
                  <div className="border-ds-border border-t p-3">
                    <Button
                      variant="secondary"
                      fullWidth
                      onClick={() => {
                        setAberto(false);
                        triggerRef.current?.focus();
                      }}
                    >
                      Fechar
                    </Button>
                  </div>
                ) : null}
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </FieldShell>
  );
};
